import { useState, useMemo, useEffect } from 'react';
import { useApi } from '../../hooks/use-api';
import { useNotify } from '../../hooks/use-notify';
import { useSingleton } from '../../hooks/use-singleton';
import { SKILL_META } from '../../utils/constant';
import { apiCommunicativeProgress, apiUserProgress } from '../../utils/api-routes';

const SKILL_KEY_MAP = {
    conversation: 'conversation',
    writing: 'writing',
    reading: 'reading',
    read_aloud: 'readAloud',
    vocabulary: 'vocabulary',
    grammar: 'grammar'
};

const toCamelSkillKey = (key) => SKILL_KEY_MAP[key] || key;

const STATUS_LABELS = {
    excellent: 'Excellent',
    good: 'Good',
    great: 'Great',
    exceptional: 'Exceptional',
    'needs improvement': 'Needs Work'
};

const normalizeStatus = (status) => {
    if (!status) return '';
    return STATUS_LABELS[status.toLowerCase()] || status;
};

const formatActivityDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });

    if (diffInDays === 0) return `Today, ${timeStr}`;
    if (diffInDays === 1) return `Yesterday, ${timeStr}`;
    if (diffInDays < 7) return `${diffInDays} days ago, ${timeStr}`;

    const dateStrFormatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${dateStrFormatted}, ${timeStr}`;
};

const useHandler = () => {
    const { get } = useApi();
    const { toggleNotify } = useNotify();
    const { mail, user } = useSingleton();

    const [overallScore, setOverallScore] = useState(0);
    const [overallStatusNote, setOverallStatusNote] = useState('');
    const [totalAttempts, setTotalAttempts] = useState(0);
    const [learningStatus, setLearningStatus] = useState({ label: '', note: '', tag: '' });
    const [skills, setSkills] = useState({});
    const [recentActivities, setRecentActivities] = useState([]);
    const [strengths, setStrengths] = useState([]);
    const [areasToImprove, setAreasToImprove] = useState([]);
    const [recommendedNextSteps, setRecommendedNextSteps] = useState([]);
    const [loading, setLoading] = useState(false);

    const userEmail = mail || user?.data?.email || '';
    const userName = user?.data?.name || (userEmail ? userEmail.split('@')[0] : '');

    const fetchProgress = async () => {
        if (!userEmail) return;

        setLoading(true);
        try {
            const res = await get(apiCommunicativeProgress(userEmail));

            if (res?.data) {
                const data = res.data;

                setOverallScore(data.overall_average || 0);
                setTotalAttempts(data.total_attempts || 0);
                setOverallStatusNote(data.summary || '');

                setLearningStatus({
                    label: data.learning_status?.label || '',
                    note: data.learning_status?.message || '',
                    tag: data.learning_status?.label || ''
                });

                const rawSkills = data.skills || {};
                const sortedActivities = Object.entries(rawSkills)
                    .filter(([_, v]) => v.last_activity)
                    .map(([key, v]) => ({ key, last_activity: v.last_activity }))
                    .sort((a, b) => new Date(b.last_activity) - new Date(a.last_activity))
                    .slice(0, 5);
                setRecentActivities(sortedActivities);

                const normalizedSkills = {};
                Object.entries(data.skills || {}).forEach(([key, value]) => {
                    normalizedSkills[toCamelSkillKey(key)] = {
                        score: value.average_score ?? 0,
                        attempts: value.attempts ?? 0,
                        status: normalizeStatus(value.status)
                    };
                });
                setSkills(normalizedSkills);

                // strengths / areas_to_improve come straight from the API now
                setStrengths(
                    (data.strengths || []).map((item) => ({
                        key: toCamelSkillKey(item.skill),
                        score: item.average_score ?? 0
                    }))
                );

                setAreasToImprove(
                    (data.areas_to_improve || []).map((item) => ({
                        key: toCamelSkillKey(item.skill),
                        score: item.average_score ?? 0
                    }))
                );
                setRecommendedNextSteps(
                    (data.recommended_next_steps || []).map((text) => {
                        const lowercaseText = text.toLowerCase();
                        // Find the first skill key whose label or key matches the text
                        const matchedKey = Object.keys(SKILL_META).find(key =>
                            lowercaseText.includes(key.toLowerCase()) ||
                            lowercaseText.includes(SKILL_META[key].label.toLowerCase())
                        );

                        return {
                            text,
                            skillKeyForIcon: matchedKey || 'conversation' // default to conversation if no match
                        };
                    })
                );
            }
        } catch (error) {
            console.error('Error fetching dashboard progress:', error);
            toggleNotify({
                type: 'error',
                title: 'Error',
                description: 'Failed to fetch dashboard progress.'
            });
        } finally {
            setLoading(false);
        }
    };

    const refreshData = () => {
        fetchProgress();
    };

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    const skillList = useMemo(() => {
        return Object.entries(skills).map(([key, value]) => ({
            key,
            ...SKILL_META[key],
            ...value
        }));
    }, [skills]);

    const strengthsWithMeta = useMemo(() => {
        return strengths.map((item) => ({
            ...item,
            ...SKILL_META[item.key]
        }));
    }, [strengths]);

    const areasToImproveWithMeta = useMemo(() => {
        return areasToImprove.map((item) => ({
            ...item,
            ...SKILL_META[item.key]
        }));
    }, [areasToImprove]);

    const activityWithMeta = useMemo(() => {
        return recentActivities.map((item) => {
            const camelKey = toCamelSkillKey(item.key);
            const meta = SKILL_META[camelKey] || {};
            return {
                label: meta.label || item.key,
                icon: meta.icon,
                color: meta.color || 'grey',
                date: formatActivityDate(item.last_activity)
            };
        });
    }, [recentActivities]);

    const nextStepsWithMeta = useMemo(() => {
        return recommendedNextSteps.map((step) => ({
            text: step.text,
            ...SKILL_META[step.skillKeyForIcon]
        }));
    }, [recommendedNextSteps]);

    const data = useMemo(() => {
        if (loading) return null;
        return {
            user: { name: userName, email: userEmail },
            overallScore,
            overallStatusLabel: learningStatus.label,
            overallStatusNote,
            totalAttempts,
            learningStatus
        };
    }, [loading, userName, userEmail, overallScore, overallStatusNote, totalAttempts, learningStatus]);

    return {
        loading,
        data,
        skillList,
        strengths: strengthsWithMeta,
        areasToImprove: areasToImproveWithMeta,
        activityWithMeta,
        nextStepsWithMeta,
        refreshData
    };
};

export default useHandler;